import { GAME_NAME, GAME_TAGLINE, STORAGE_KEYS, type QualityLevel } from '../config';
import { TIERS } from '../art/tiers';
import { ScoreSystem, type HighScoreEntry } from '../game/ScoreSystem';

export type Screen = 'menu' | 'tutorial' | 'playing' | 'paused' | 'gameover' | 'settings' | 'scores' | 'hidden';

export interface SettingsState {
  music: boolean;
  sfx: boolean;
  volume: number;
  quality: QualityLevel;
}

export interface UICallbacks {
  onStart: () => void;
  onResume: () => void;
  onRestart: () => void;
  onPause: () => void;
  onOpenSettings: () => void;
  onOpenScores: () => void;
  onOpenTutorial: () => void;
  onBackToMenu: () => void;
  onSettingsChange: (s: SettingsState) => void;
}

export class UIManager {
  private root: HTMLElement;
  private cbs: UICallbacks;
  private screen: Screen = 'menu';
  settings: SettingsState;

  private hudEl!: HTMLElement;
  private scoreEl!: HTMLElement;
  private scoreGainEl!: HTMLElement;
  private bestEl!: HTMLElement;
  private nextNameEl!: HTMLElement;
  private nextSwatchEl!: HTMLElement;
  private comboEl!: HTMLElement;
  private dangerEl!: HTMLElement;
  private hintEl!: HTMLElement;

  private menuEl!: HTMLElement;
  private tutorialEl!: HTMLElement;
  private pauseEl!: HTMLElement;
  private gameoverEl!: HTMLElement;
  private settingsEl!: HTMLElement;
  private scoresEl!: HTMLElement;

  private goScoreEl!: HTMLElement;
  private goTierEl!: HTMLElement;
  private goMergesEl!: HTMLElement;
  private goBestBadge!: HTMLElement;

  constructor(root: HTMLElement, cbs: UICallbacks) {
    this.root = root;
    this.cbs = cbs;
    this.settings = this.loadSettings();
    this.build();
    this.show('menu');
  }

  private loadSettings(): SettingsState {
    try {
      const raw = localStorage.getItem(STORAGE_KEYS.settings);
      if (raw) return { ...{ music: true, sfx: true, volume: 0.7, quality: 'high' as QualityLevel }, ...JSON.parse(raw) };
    } catch {
      /* ignore */
    }
    return { music: true, sfx: true, volume: 0.7, quality: 'high' };
  }

  saveSettings(): void {
    localStorage.setItem(STORAGE_KEYS.settings, JSON.stringify(this.settings));
  }

  private build(): void {
    this.root.innerHTML = '';

    // HUD
    this.hudEl = el('div', 'hud');
    this.hudEl.innerHTML = `
      <div class="hud-top">
        <div class="hud-card">
          <div class="label">Score</div>
          <div class="value" id="ui-score">0</div>
          <div class="sub" id="ui-score-gain"></div>
        </div>
        <div class="hud-card next-preview">
          <div>
            <div class="label">Next</div>
            <div class="sub" id="ui-next-name">—</div>
          </div>
          <div class="next-swatch" id="ui-next-swatch"></div>
        </div>
        <div style="display:flex;flex-direction:column;gap:8px;align-items:flex-end">
          <div class="hud-card">
            <div class="label">Best</div>
            <div class="value" id="ui-best" style="font-size:1.2rem">0</div>
          </div>
          <div class="hud-actions">
            <button class="icon-btn" id="btn-pause" title="Pause" aria-label="Pause">❚❚</button>
            <button class="icon-btn" id="btn-settings-h" title="Settings" aria-label="Settings">⚙</button>
          </div>
        </div>
      </div>
      <div class="combo-banner" id="ui-combo"></div>
      <div class="danger-timer" id="ui-danger"></div>
      <div class="drop-hint" id="ui-hint">Drag to aim · Tap / Space to drop</div>
    `;
    this.root.appendChild(this.hudEl);
    this.scoreEl = this.hudEl.querySelector('#ui-score')!;
    this.scoreGainEl = this.hudEl.querySelector('#ui-score-gain')!;
    this.bestEl = this.hudEl.querySelector('#ui-best')!;
    this.nextNameEl = this.hudEl.querySelector('#ui-next-name')!;
    this.nextSwatchEl = this.hudEl.querySelector('#ui-next-swatch')!;
    this.comboEl = this.hudEl.querySelector('#ui-combo')!;
    this.dangerEl = this.hudEl.querySelector('#ui-danger')!;
    this.hintEl = this.hudEl.querySelector('#ui-hint')!;

    this.hudEl.querySelector('#btn-pause')!.addEventListener('click', () => this.cbs.onPause());
    this.hudEl.querySelector('#btn-settings-h')!.addEventListener('click', () => this.cbs.onOpenSettings());

    // Menu
    this.menuEl = this.makeOverlay('menu');
    this.menuEl.innerHTML = `
      <div class="panel interactive">
        <div class="menu-hero" aria-hidden="true"></div>
        <h1>${GAME_NAME}</h1>
        <p class="tagline">${GAME_TAGLINE}</p>
        <div class="evo-row" id="menu-evo"></div>
        <div class="btn-row">
          <button class="btn btn-primary" id="btn-play">Play</button>
          <button class="btn btn-secondary" id="btn-how">How to Play</button>
          <button class="btn btn-secondary" id="btn-scores">High Scores</button>
          <button class="btn btn-ghost" id="btn-settings">Settings</button>
        </div>
      </div>
    `;
    const evo = this.menuEl.querySelector('#menu-evo')!;
    TIERS.forEach((t) => {
      const d = document.createElement('div');
      d.className = 'evo-dot';
      d.title = t.name;
      d.style.background = `#${t.color.toString(16).padStart(6, '0')}`;
      evo.appendChild(d);
    });
    this.menuEl.querySelector('#btn-play')!.addEventListener('click', () => this.cbs.onStart());
    this.menuEl.querySelector('#btn-how')!.addEventListener('click', () => this.cbs.onOpenTutorial());
    this.menuEl.querySelector('#btn-scores')!.addEventListener('click', () => this.cbs.onOpenScores());
    this.menuEl.querySelector('#btn-settings')!.addEventListener('click', () => this.cbs.onOpenSettings());
    this.root.appendChild(this.menuEl);

    // Tutorial
    this.tutorialEl = this.makeOverlay('tutorial');
    this.tutorialEl.innerHTML = `
      <div class="panel interactive">
        <h2>How to Play</h2>
        <div class="tutorial-step"><strong>1. Aim & Drop</strong><span>Move to position the next confection, then drop it into the jar.</span></div>
        <div class="tutorial-step"><strong>2. Match to Merge</strong><span>Two of the same kind fuse into the next celestial sweet.</span></div>
        <div class="tutorial-step"><strong>3. Chain Reactions</strong><span>Big combos score bonus points — keep the merges rolling!</span></div>
        <div class="tutorial-step"><strong>4. Mind the Line</strong><span>If sweets linger above the danger line, the jar overflows.</span></div>
        <div class="tutorial-step"><strong>5. Reach Aethercake</strong><span>Evolve through 11 wonders and chase the high score.</span></div>
        <div class="btn-row">
          <button class="btn btn-primary" id="btn-tut-play">Got it — Play</button>
          <button class="btn btn-ghost" id="btn-tut-back">Back</button>
        </div>
      </div>
    `;
    this.tutorialEl.querySelector('#btn-tut-play')!.addEventListener('click', () => {
      localStorage.setItem(STORAGE_KEYS.tutorialSeen, '1');
      this.cbs.onStart();
    });
    this.tutorialEl.querySelector('#btn-tut-back')!.addEventListener('click', () => this.cbs.onBackToMenu());
    this.root.appendChild(this.tutorialEl);

    // Pause
    this.pauseEl = this.makeOverlay('paused');
    this.pauseEl.innerHTML = `
      <div class="panel interactive">
        <h2>Paused</h2>
        <p>The cosmos can wait a moment.</p>
        <div class="btn-row">
          <button class="btn btn-primary" id="btn-resume">Resume</button>
          <button class="btn btn-secondary" id="btn-restart-p">Restart</button>
          <button class="btn btn-secondary" id="btn-settings-p">Settings</button>
          <button class="btn btn-ghost" id="btn-menu-p">Main Menu</button>
        </div>
      </div>
    `;
    this.pauseEl.querySelector('#btn-resume')!.addEventListener('click', () => this.cbs.onResume());
    this.pauseEl.querySelector('#btn-restart-p')!.addEventListener('click', () => this.cbs.onRestart());
    this.pauseEl.querySelector('#btn-settings-p')!.addEventListener('click', () => this.cbs.onOpenSettings());
    this.pauseEl.querySelector('#btn-menu-p')!.addEventListener('click', () => this.cbs.onBackToMenu());
    this.root.appendChild(this.pauseEl);

    // Game over
    this.gameoverEl = this.makeOverlay('gameover');
    this.gameoverEl.innerHTML = `
      <div class="panel interactive">
        <div class="best-badge" id="go-best-badge" style="display:none">New Best!</div>
        <h2>Jar Overflow</h2>
        <p>Your confections rose too high — but what a journey.</p>
        <div class="go-stats">
          <div class="go-stat"><div class="n" id="go-score">0</div><div class="l">Score</div></div>
          <div class="go-stat"><div class="n" id="go-tier">—</div><div class="l">Best Merge</div></div>
          <div class="go-stat"><div class="n" id="go-merges">0</div><div class="l">Merges</div></div>
          <div class="go-stat"><div class="n" id="go-combo">0</div><div class="l">Max Chain</div></div>
        </div>
        <div class="btn-row">
          <button class="btn btn-primary" id="btn-retry">Play Again</button>
          <button class="btn btn-secondary" id="btn-go-scores">High Scores</button>
          <button class="btn btn-ghost" id="btn-go-menu">Main Menu</button>
        </div>
      </div>
    `;
    this.goScoreEl = this.gameoverEl.querySelector('#go-score')!;
    this.goTierEl = this.gameoverEl.querySelector('#go-tier')!;
    this.goMergesEl = this.gameoverEl.querySelector('#go-merges')!;
    this.goBestBadge = this.gameoverEl.querySelector('#go-best-badge')!;
    this.gameoverEl.querySelector('#btn-retry')!.addEventListener('click', () => this.cbs.onRestart());
    this.gameoverEl.querySelector('#btn-go-scores')!.addEventListener('click', () => this.cbs.onOpenScores());
    this.gameoverEl.querySelector('#btn-go-menu')!.addEventListener('click', () => this.cbs.onBackToMenu());
    this.root.appendChild(this.gameoverEl);

    // Settings
    this.settingsEl = this.makeOverlay('settings');
    this.settingsEl.innerHTML = `
      <div class="panel interactive">
        <h2>Settings</h2>
        <div class="settings-row">
          <label>Music</label>
          <button class="toggle ${this.settings.music ? 'on' : ''}" id="tog-music" aria-label="Toggle music"></button>
        </div>
        <div class="settings-row">
          <label>Sound FX</label>
          <button class="toggle ${this.settings.sfx ? 'on' : ''}" id="tog-sfx" aria-label="Toggle SFX"></button>
        </div>
        <div class="settings-row">
          <label>Volume</label>
          <input type="range" id="vol" min="0" max="1" step="0.05" value="${this.settings.volume}" />
        </div>
        <div class="settings-row">
          <label>Quality</label>
          <select id="quality">
            <option value="low" ${this.settings.quality === 'low' ? 'selected' : ''}>Low</option>
            <option value="medium" ${this.settings.quality === 'medium' ? 'selected' : ''}>Medium</option>
            <option value="high" ${this.settings.quality === 'high' ? 'selected' : ''}>High</option>
          </select>
        </div>
        <div class="btn-row">
          <button class="btn btn-primary" id="btn-set-back">Done</button>
        </div>
      </div>
    `;
    const syncToggles = () => {
      this.settingsEl.querySelector('#tog-music')!.classList.toggle('on', this.settings.music);
      this.settingsEl.querySelector('#tog-sfx')!.classList.toggle('on', this.settings.sfx);
    };
    this.settingsEl.querySelector('#tog-music')!.addEventListener('click', () => {
      this.settings.music = !this.settings.music;
      syncToggles();
      this.persistSettings();
    });
    this.settingsEl.querySelector('#tog-sfx')!.addEventListener('click', () => {
      this.settings.sfx = !this.settings.sfx;
      syncToggles();
      this.persistSettings();
    });
    this.settingsEl.querySelector('#vol')!.addEventListener('input', (e) => {
      this.settings.volume = parseFloat((e.target as HTMLInputElement).value);
      this.persistSettings();
    });
    this.settingsEl.querySelector('#quality')!.addEventListener('change', (e) => {
      this.settings.quality = (e.target as HTMLSelectElement).value as QualityLevel;
      this.persistSettings();
    });
    this.settingsEl.querySelector('#btn-set-back')!.addEventListener('click', () => {
      if (this.screen === 'settings') {
        // return handled by parent via back — use pause or menu
        this.cbs.onBackToMenu();
      }
    });
    // Better done: resume if was playing
    this.settingsEl.querySelector('#btn-set-back')!.addEventListener('click', () => {
      /* onBackToMenu may be wrong; Game will handle screen stack */
    });
    this.root.appendChild(this.settingsEl);

    // Scores
    this.scoresEl = this.makeOverlay('scores');
    this.scoresEl.innerHTML = `
      <div class="panel interactive">
        <h2>High Scores</h2>
        <ul class="score-list" id="score-list"></ul>
        <div class="btn-row">
          <button class="btn btn-primary" id="btn-sc-back">Back</button>
        </div>
      </div>
    `;
    this.scoresEl.querySelector('#btn-sc-back')!.addEventListener('click', () => this.cbs.onBackToMenu());
    this.root.appendChild(this.scoresEl);

    this.updateBestDisplay();
  }

  private persistSettings(): void {
    this.saveSettings();
    this.cbs.onSettingsChange(this.settings);
  }

  private makeOverlay(id: string): HTMLElement {
    const o = el('div', `overlay interactive`);
    o.id = `overlay-${id}`;
    return o;
  }

  show(screen: Screen): void {
    this.screen = screen;
    const map: Record<string, HTMLElement | undefined> = {
      menu: this.menuEl,
      tutorial: this.tutorialEl,
      paused: this.pauseEl,
      gameover: this.gameoverEl,
      settings: this.settingsEl,
      scores: this.scoresEl,
    };
    for (const [k, node] of Object.entries(map)) {
      node?.classList.toggle('visible', k === screen);
    }
    this.hudEl.style.visibility = screen === 'playing' || screen === 'paused' ? 'visible' : 'hidden';
    this.hudEl.style.opacity = screen === 'playing' ? '1' : screen === 'paused' ? '0.5' : '0';
    document.body.classList.toggle('paused', screen === 'paused');

    if (screen === 'scores') this.renderScores();
    if (screen === 'settings') {
      // fix Done button to go back appropriately
      const done = this.settingsEl.querySelector('#btn-set-back') as HTMLButtonElement;
      done.onclick = () => {
        // Game manages stack — emit resume-like back
        this.cbs.onBackToMenu();
      };
    }
  }

  getScreen(): Screen {
    return this.screen;
  }

  updateHud(opts: {
    score: number;
    lastGain: number;
    lastGainTimer: number;
    combo: number;
    nextTier: number;
    dangerRatio: number;
    showHint: boolean;
  }): void {
    this.scoreEl.textContent = opts.score.toLocaleString();
    if (opts.lastGainTimer > 0 && opts.lastGain > 0) {
      this.scoreGainEl.textContent = `+${opts.lastGain}`;
    } else {
      this.scoreGainEl.textContent = '';
    }
    if (opts.combo >= 2) {
      this.comboEl.textContent = `COMBO ×${opts.combo}`;
      this.comboEl.classList.add('show');
    } else {
      this.comboEl.classList.remove('show');
    }
    const tier = TIERS[opts.nextTier];
    if (tier) {
      this.nextNameEl.textContent = tier.name;
      this.nextSwatchEl.style.background = `radial-gradient(circle at 35% 30%, #ffffff88, #${tier.color.toString(16).padStart(6, '0')})`;
    }
    if (opts.dangerRatio > 0.15) {
      this.dangerEl.textContent = opts.dangerRatio > 0.85 ? 'OVERFLOW!' : 'Too high!';
      this.dangerEl.classList.add('show');
    } else {
      this.dangerEl.classList.remove('show');
    }
    this.hintEl.style.opacity = opts.showHint ? '0.85' : '0';
  }

  showGameOver(data: {
    score: number;
    maxTier: number;
    merges: number;
    chain: number;
    isNewBest: boolean;
  }): void {
    this.goScoreEl.textContent = data.score.toLocaleString();
    this.goTierEl.textContent = TIERS[data.maxTier]?.name ?? '—';
    this.goMergesEl.textContent = String(data.merges);
    const comboEl = this.gameoverEl.querySelector('#go-combo')!;
    comboEl.textContent = String(data.chain);
    this.goBestBadge.style.display = data.isNewBest ? 'inline-block' : 'none';
    this.updateBestDisplay();
    this.show('gameover');
  }

  private renderScores(): void {
    const list = this.scoresEl.querySelector('#score-list')!;
    const scores = ScoreSystem.loadHighScores();
    if (scores.length === 0) {
      list.innerHTML = `<li style="justify-content:center;color:var(--muted)">No scores yet — go make magic!</li>`;
      return;
    }
    list.innerHTML = scores
      .map(
        (s: HighScoreEntry, i: number) =>
          `<li><span class="rank">#${i + 1}</span><span>${TIERS[s.maxTier]?.name ?? ''}</span><span class="pts">${s.score.toLocaleString()}</span></li>`,
      )
      .join('');
  }

  updateBestDisplay(): void {
    const scores = ScoreSystem.loadHighScores();
    this.bestEl.textContent = (scores[0]?.score ?? 0).toLocaleString();
  }

  shouldShowTutorialFirst(): boolean {
    return !localStorage.getItem(STORAGE_KEYS.tutorialSeen);
  }
}

function el<K extends keyof HTMLElementTagNameMap>(tag: K, className?: string): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (className) e.className = className;
  return e;
}
